import { supabase } from '@/lib/supabase';
import { getOrganizationByCommerce } from '@/lib/zifekCore';

// ─────────────────────────────────────────────────────────────
// ZIFEK — Avis des commerces / vendeurs (dans la fiche du chat IA).
//
// Réutilise l'infrastructure d'avis du socle (core_reviews) en la
// rattachant à la BOUTIQUE (users.id) plutôt qu'à une organisation,
// afin de fonctionner pour TOUS les vendeurs — y compris ceux qui
// n'ont pas encore d'organisation du socle.
//
// Clé d'entité : entity_type = 'vendor_shop', entity_id = String(commerceId).
// L'organization_id est renseigné quand il existe (agrégation dashboard).
//
// Chaque avis porte :
//   • une note (1 à 5 étoiles, cliquables)         → ranking des meilleurs
//   • un sentiment : positif / mitige / negatif
//   • un commentaire libre
// ─────────────────────────────────────────────────────────────

export type ReviewSentiment = 'positif' | 'mitige' | 'negatif';

export const REVIEW_SENTIMENTS: { key: ReviewSentiment; label: string; icon: string; className: string }[] = [
  { key: 'positif', label: 'Positif', icon: 'ri-emotion-happy-line', className: 'bg-accent-100 text-accent-800 border-accent-300' },
  { key: 'mitige', label: 'Mitigé', icon: 'ri-emotion-normal-line', className: 'bg-secondary-100 text-secondary-800 border-secondary-300' },
  { key: 'negatif', label: 'Négatif', icon: 'ri-emotion-unhappy-line', className: 'bg-red-50 text-red-700 border-red-300' },
];

export function sentimentMeta(key: string | null | undefined) {
  return REVIEW_SENTIMENTS.find((s) => s.key === key) || null;
}

export interface VendorReview {
  id: number;
  authorUserId: number | null;
  authorName: string;
  rating: number;
  sentiment: ReviewSentiment | null;
  comment: string;
  createdAt: string | null;
}

const ENTITY_TYPE = 'vendor_shop';

function mapReview(row: Record<string, unknown>): VendorReview {
  const rawSentiment = (row.sentiment as string) || '';
  const sentiment = (['positif', 'mitige', 'negatif'] as string[]).includes(rawSentiment)
    ? (rawSentiment as ReviewSentiment)
    : null;
  return {
    id: Number(row.id),
    authorUserId: row.author_user_id != null ? Number(row.author_user_id) : null,
    authorName: (row.author_name as string) || 'Anonyme',
    rating: Number(row.rating) || 0,
    sentiment,
    comment: (row.comment as string) || '',
    createdAt: (row.created_at as string) || null,
  };
}

/** Avis publiés d'une boutique (vendeur / commerce). */
export async function listVendorReviews(commerceId: number, limit = 60): Promise<VendorReview[]> {
  if (!commerceId) return [];
  try {
    const { data, error } = await supabase
      .from('core_reviews')
      .select('id, author_user_id, author_name, rating, comment, sentiment, created_at')
      .eq('entity_type', ENTITY_TYPE)
      .eq('entity_id', String(commerceId))
      .eq('status', 'published')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error || !data) return [];
    return (data as Record<string, unknown>[]).map(mapReview);
  } catch {
    return [];
  }
}

export interface CreateVendorReviewInput {
  commerceId: number;
  authorUserId?: number | null;
  authorName: string;
  rating: number;
  sentiment: ReviewSentiment | null;
  comment?: string | null;
}

/**
 * Dépose un avis sur une boutique. L'avis est publié immédiatement (il sert
 * au ranking des meilleurs commerces/vendeurs) et rattaché à l'organisation
 * du vendeur quand elle existe.
 */
export async function createVendorReview(input: CreateVendorReviewInput): Promise<void> {
  const { commerceId } = input;
  if (!commerceId) throw new Error('Commerce introuvable.');
  const name = (input.authorName || '').trim();
  if (!name) throw new Error('Merci d’indiquer votre nom.');

  let organizationId: number | null = null;
  let tenantId: number | null = null;
  try {
    const org = await getOrganizationByCommerce(commerceId);
    if (org) {
      organizationId = org.id;
      tenantId = org.tenant_id ?? null;
    }
  } catch {
    /* sans organisation : l'avis reste rattaché à la boutique */
  }

  const { error } = await supabase.from('core_reviews').insert({
    tenant_id: tenantId,
    organization_id: organizationId,
    author_user_id: input.authorUserId ?? null,
    author_name: name,
    entity_type: ENTITY_TYPE,
    entity_id: String(commerceId),
    rating: Math.max(1, Math.min(5, Math.round(input.rating) || 5)),
    comment: (input.comment || '').trim() || null,
    sentiment: input.sentiment,
    status: 'published',
    is_verified: false,
    is_factual: false,
    helpful_count: 0,
    report_count: 0,
  });
  if (error) throw error;
}

export interface VendorRating {
  average: number;
  count: number;
}

/** Note moyenne publiée d'une boutique. */
export async function fetchVendorRating(commerceId: number): Promise<VendorRating> {
  const reviews = await listVendorReviews(commerceId, 500);
  const valid = reviews.filter((r) => r.rating > 0);
  if (valid.length === 0) return { average: 0, count: 0 };
  const sum = valid.reduce((acc, r) => acc + r.rating, 0);
  return { average: Math.round((sum / valid.length) * 10) / 10, count: valid.length };
}

/**
 * Notes moyennes de plusieurs boutiques d'un coup (pour le classement des
 * meilleurs commerces/vendeurs sur une marketplace ou un dashboard).
 */
export async function fetchVendorRatings(commerceIds: number[]): Promise<Record<number, VendorRating>> {
  const unique = Array.from(new Set(commerceIds.filter((n) => Number.isFinite(n) && n > 0)));
  const result: Record<number, VendorRating> = {};
  if (unique.length === 0) return result;
  try {
    const { data, error } = await supabase
      .from('core_reviews')
      .select('entity_id, rating')
      .eq('entity_type', ENTITY_TYPE)
      .eq('status', 'published')
      .in('entity_id', unique.map((n) => String(n)));
    if (error || !data) return result;
    const acc: Record<number, { sum: number; count: number }> = {};
    (data as { entity_id: string | null; rating: number | null }[]).forEach((row) => {
      const id = Number(row.entity_id);
      const rating = Number(row.rating) || 0;
      if (!id || rating <= 0) return;
      if (!acc[id]) acc[id] = { sum: 0, count: 0 };
      acc[id].sum += rating;
      acc[id].count += 1;
    });
    Object.keys(acc).forEach((k) => {
      const id = Number(k);
      result[id] = { average: Math.round((acc[id].sum / acc[id].count) * 10) / 10, count: acc[id].count };
    });
  } catch {
    /* best-effort */
  }
  return result;
}

/** Date lisible (jj/mm/aaaa). */
export function formatVendorReviewDate(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}